"use client";

import React from "react";

import Section from "@/components/common/Section/Section";
import { ButtonNova, ButtonNovaProps } from "@/components/ui-kit/ButtonNova/ButtonNova";
import { TextElementNova } from "@/components/ui-kit/TextElementNova/TextElementNova";
import { StatsProps } from "@/types/stats";
import { cn, scrollToId } from "@/utils/general";

import ScrollStat from "./ScrollStat";

const ScrollToProductsButton = (props: ButtonNovaProps) => (
  <ButtonNova
    onClick={() => scrollToId("products")}
    level="Tertiary"
    icon="Right"
    copyClassName="text-nova-base-white"
    {...props}
  />
);

const Stats = ({ data }: StatsProps) => {
  const {
    heading,
    headingWidths = "",
    copy,
    copyWidths,
    flexHeading,
    flexSubHeading,
    leftBoxWidths,
    rightBoxWidths,
    button,
    statsIntro,
    stats,
    statWidth = "w-[106px] lg:w-[191px]",
    colorMode = "dark",
    mb = "mb-28 md:mb-[56px] xl:mb-[128px]",
    statsWrapperClasses,
    scrollToProductsButton,
  } = data;

  const color = colorMode === "dark" ? "text-nova-base-white" : "text-nova-base-black";

  return (
    <div className={cn(colorMode === "dark" && "bg-nova-base-black")}>
      <Section>
        <div className={cn(mb, flexHeading ? "md:hidden" : "")}>
          <div className={`${headingWidths ? headingWidths : ""}`}>
            <TextElementNova
              text={heading || flexHeading}
              element="h2"
              className={cn("Title-20-Super-Normal", color, button ? "mb-[24px]" : "")}
            />
          </div>

          {copy ? (
            <div className={cn("mt-4", copyWidths)}>
              <TextElementNova
                text={copy}
                element="p"
                className={cn("Subtitle-10-Dia-Normal", color)}
              />
            </div>
          ) : null}

          {button ? (
            <div className="flex">
              <ButtonNova
                ctaLink={button.destination}
                label={button.label}
                icon={`Left`}
                level="Tertiary"
                copyClassName="text-nova-base-white"
                trackObject={{
                  destination: button.destination,
                  type: "link",
                  text: button.label,
                }}
              />
            </div>
          ) : null}
        </div>

        <div className={cn("flex flex-col md:flex-row", statsWrapperClasses)}>
          <div
            className={cn(
              "md:flex md:flex-col",
              leftBoxWidths ? leftBoxWidths : "w-full lg:w-1/2 xl:w-[58%]",
              flexHeading ? "" : "mb-6 lg:mb-0"
            )}
          >
            {flexHeading ? (
              <div className={cn(headingWidths)}>
                <TextElementNova
                  text={flexHeading}
                  element="p"
                  className={cn(
                    "Title-20-Super-Normal hidden md:block flex-heading lg:pr-2 xl:pr-6",
                    color
                  )}
                />
              </div>
            ) : null}

            {flexSubHeading ? (
              <div className={`pt-4 hidden md:block pr-6`}>
                <TextElementNova
                  text={flexSubHeading}
                  element="p"
                  className={cn("Body-30-Dia-Normal", color)}
                />
              </div>
            ) : null}

            {statsIntro ? (
              <TextElementNova
                text={statsIntro}
                element="p"
                className={cn("Body-30-Dia-Strong", color, "sm:mt-auto md:w-[320px]")}
              />
            ) : null}

            {/* Scroll to products button - desktop only */}
            {!!scrollToProductsButton ? (
              <div className="hidden lg:block mt-auto">
                <ScrollToProductsButton {...scrollToProductsButton} />
              </div>
            ) : null}
          </div>

          <div
            className={cn(
              "w-full md:w-full lg:w-1/2 xl:w-[42%]",
              rightBoxWidths ? rightBoxWidths : "w-full lg:w-1/2 xl:w-[42%]"
            )}
          >
            {stats.map((stat, i) => (
              <React.Fragment key={i}>
                <ScrollStat
                  stat={stat}
                  statsLength={stats.length}
                  i={i}
                  colorMode={colorMode}
                  statWidth={statWidth}
                  moduleName={flexHeading || heading}
                />
              </React.Fragment>
            ))}
          </div>

          {/* Scroll to products button - mobile only */}
          {!!scrollToProductsButton ? (
            <div className="lg:hidden mt-10 md:mt-12 mx-auto">
              <ScrollToProductsButton {...scrollToProductsButton} />
            </div>
          ) : null}
        </div>
      </Section>
    </div>
  );
};

export default Stats;
